import type { FC } from 'react';
import { useRef, useState } from 'react';
import { Show, ShowFirstMatching } from '@components/Show';
import { useEntityStore } from '@ContractBuilder/store';
import { ResourceState } from '@domain/schemas/contract.schema';
import type { DbTag } from '@domain/schemas/tag.schema';
import type { EntityData } from '@domain/types/entity';
import { useClosePopoverOnScrollOut } from '@hooks/use-close-popover-on-scroll-out';
import { fetchEntity } from '@queries/fetchEntity';
import { submissionQueries } from '@queries/fetchSubmissions';
import { templateQueries } from '@queries/fetchTemplates';
import { isNonNullish } from '@root/helpers';
import { useDocumentLocation } from '@src/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { get, xor } from 'lodash-es';

import type { TagComponentProps } from './tags';
import { AddTagsButton, MultipleTagsButton, SingleTagButton } from './tags';

interface ItemTagsProps {
  item: EntityData;
  tags: DbTag[];
}

const ItemTags: FC<ItemTagsProps> = ({ item, tags }) => {
  const [open, setOpen] = useState(false);
  const [updatedTagsIds, setUpdatedTagsIds] = useState<string[]>();

  const { isTemplate } = useDocumentLocation();

  const savedTagsIds = (updatedTagsIds ?? get(item, 'tags', []))?.filter((tagId: string) =>
    tags.some((tag: DbTag) => tag.id === tagId),
  );
  const savedTagsLabels = savedTagsIds
    .map((tagId: string) => tags.find((tag: DbTag) => tag.id === tagId)?.label)
    .filter(isNonNullish);

  const [editingTagsIds, setEditingTagsIds] = useState<string[]>(savedTagsIds);
  const [isUpdatingItemTags, setIsUpdatingItemTags] = useState(false);

  const queryClient = useQueryClient();
  const updateSubmission = useEntityStore(({ updateResourceData }) => updateResourceData);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      setEditingTagsIds(savedTagsIds);
    }
  };

  const handleChangeItemTags = (newTagsIds?: string[]) => setEditingTagsIds(newTagsIds ?? []);

  const handleCancelUpdateItemTags = () => {
    setEditingTagsIds(savedTagsIds);
    setOpen(false);
  };

  const handleUpdateItemTags = async () => {
    const payload: string[] = editingTagsIds;

    try {
      setIsUpdatingItemTags(true);

      await updateSubmission({ tags: payload }, item.id);
      const { tags: tagsResponse = [] } = await fetchEntity({ dataRange: [], entityId: item.id });
      setUpdatedTagsIds(tagsResponse);

      const patchTags = (entry: EntityData) => (entry.id === item.id ? { ...entry, tags: tagsResponse } : entry);

      if (isTemplate) {
        // Templates listing is DDB-backed — a flat array under the exact list key.
        queryClient.setQueryData<EntityData[]>(templateQueries.list().queryKey, (old) => old?.map(patchTags));
      } else {
        // The OpenSearch index reindexes asynchronously, so this keeps
        // the display fresh immediately (the server-side tag filter stays eventually consistent).
        const [submissionsRoot] = submissionQueries.list().queryKey;
        queryClient.setQueriesData({ queryKey: [submissionsRoot] }, (old: unknown) => {
          // source=ddb
          if (Array.isArray(old)) {
            return (old as EntityData[]).map(patchTags);
          }

          // opensearch / infinite loading
          const infinite = old as { pages?: { data: EntityData[] }[] } | undefined;

          if (infinite?.pages) {
            return { ...infinite, pages: infinite.pages.map((page) => ({ ...page, data: page.data?.map(patchTags) })) };
          }
          return old;
        });
      }

      setOpen(false);
    } catch (_error) {
      setEditingTagsIds(savedTagsIds);
    } finally {
      setIsUpdatingItemTags(false);
    }
  };

  const hasChanges = xor(editingTagsIds, savedTagsIds).length > 0;

  const popoverTriggerRef = useRef(null);
  useClosePopoverOnScrollOut(popoverTriggerRef, open, setOpen);

  const tagButtonCommonProps: TagComponentProps = {
    handleCancelUpdateItemTags,
    handleChangeItemTags,
    handleUpdateItemTags,
    hasChanges,
    isUpdatingItemTags,
    itemTagsIds: editingTagsIds,
    savedTagsLabels,
    open,
    setOpen: handleOpenChange,
    tags,
    popoverTriggerRef,
  };

  return (
    <ShowFirstMatching>
      <Show when={savedTagsLabels.length === 0 && item.status !== ResourceState.PRE_DRAFT}>
        <AddTagsButton {...tagButtonCommonProps} />
      </Show>
      <Show when={savedTagsLabels.length === 1}>
        <SingleTagButton {...tagButtonCommonProps} />
      </Show>
      <Show when={savedTagsLabels.length > 1}>
        <MultipleTagsButton {...tagButtonCommonProps} />
      </Show>
    </ShowFirstMatching>
  );
};

export default ItemTags;
